/**
 * Film Comps Data Types and Validation
 * Defines all data structures and validation schemas for the Film Comps system
 */

// ==================== CORE DATA TYPES ====================

/**
 * @typedef {Object} MatchingCriteria
 * @property {number} genre - Genre matching score (0-1)
 * @property {number} creative - Creative matching score (0-1)
 * @property {number} studio - Studio matching score (0-1)
 * @property {number} seasonal - Seasonal matching score (0-1)
 * @property {number} budget - Budget matching score (0-1)
 */

/**
 * @typedef {Object} ModeledComp
 * @deprecated Modeled comps have been temporarily removed from the Film Comps feature.
 * They may be re-added in the future when the model improves.
 * @property {string} id - Unique comp ID
 * @property {string} compFilmId - IMDB ID of the comp film
 * @property {string} compFilmTitle - Title of the comp film
 * @property {number} confidenceScore - Overall confidence score (0-1)
 * @property {MatchingCriteria} matchingCriteria - Detailed matching criteria scores
 * @property {string} createdAt - ISO timestamp of creation
 */

/**
 * @typedef {Object} GreenlightComp
 * @property {string} id - Unique comp ID
 * @property {string} compFilmId - IMDB ID of the comp film
 * @property {string} compFilmTitle - Title of the comp film
 * @property {string} assignedBy - User ID of the assigner
 * @property {string} assignedAt - ISO timestamp of assignment
 * @property {string} reason - Reason for assignment
 * @property {boolean} isActive - Whether the comp is active
 */

/**
 * @typedef {Object} FilmCompsResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {string} data.filmId - IMDB ID of the film
 * @property {string} data.filmTitle - Title of the film
 * @property {ModeledComp[]} data.modeledComps - Array of modeled comps
 * @property {GreenlightComp[]} data.greenlightComps - Array of greenlight comps
 * @property {number} data.totalCount - Total number of comps
 * @property {number} data.modeledCount - Number of modeled comps
 * @property {number} data.greenlightCount - Number of greenlight comps
 */

/**
 * @typedef {Object} ComparisonMetrics
 * @property {number} daysDifference - Difference in days until release
 * @property {number} weeksDifference - Difference in weeks until release
 * @property {number} runtimeDifference - Difference in runtime (seconds)
 * @property {number} releaseYearDifference - Difference in release year
 */

/**
 * @typedef {Object} CompWithMetrics
 * @property {string} compId - Unique comp ID
 * @property {string} compFilmId - IMDB ID of the comp film
 * @property {string} compFilmTitle - Title of the comp film
 * @property {string} compType - Type of comp ('modeled' or 'greenlight')
 * @property {number} confidenceScore - Confidence score (for modeled comps)
 * @property {string} assignedBy - User ID of assigner (for greenlight comps)
 * @property {string} assignedAt - Assignment timestamp (for greenlight comps)
 * @property {MatchingCriteria} matchingCriteria - Matching criteria scores
 * @property {ComparisonMetrics} comparisonMetrics - Comparison metrics
 */

/**
 * @typedef {Object} PrimaryFilm
 * @property {string} filmId - IMDB ID of the film
 * @property {string} title - Title of the film
 * @property {string} releaseDate - Release date (YYYY-MM-DD)
 * @property {number} runtime - Runtime in seconds
 * @property {string} productionStatus - Production status
 * @property {string[]} categories - Array of categories/genres
 */

/**
 * @typedef {Object} ComparisonDataResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {PrimaryFilm} data.primaryFilm - Primary film information
 * @property {CompWithMetrics[]} data.comps - Array of comps with metrics
 * @property {Object} data.summary - Summary statistics
 * @property {number} data.summary.totalComps - Total number of comps
 * @property {number} data.summary.modeledComps - Number of modeled comps
 * @property {number} data.summary.greenlightComps - Number of greenlight comps
 * @property {number} data.summary.averageConfidenceScore - Average confidence score
 */

/**
 * @typedef {Object} CompCandidate
 * @property {string} filmId - IMDB ID of the candidate film
 * @property {string} title - Title of the candidate film
 * @property {number} releaseYear - Release year
 * @property {number} runtime - Runtime in seconds
 * @property {string} productionStatus - Production status
 * @property {number} matchScore - Overall match score (0-1)
 * @property {MatchingCriteria} matchingCriteria - Detailed matching criteria scores
 */

/**
 * @typedef {Object} CandidatesResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {CompCandidate[]} data.candidates - Array of candidate films
 * @property {number} data.totalCount - Total number of candidates available
 */

/**
 * @typedef {Object} CompAssignment
 * @property {string} primaryFilmId - IMDB ID of the primary film
 * @property {string} compFilmId - IMDB ID of the comp film
 * @property {string} reason - Reason for assignment
 * @property {string} assignedBy - User ID of the assigner
 */

/**
 * @typedef {Object} AssignmentResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {string} data.id - Unique comp ID
 * @property {string} data.primaryFilmId - IMDB ID of the primary film
 * @property {string} data.compFilmId - IMDB ID of the comp film
 * @property {string} data.compType - Type of comp ('greenlight')
 * @property {string} data.assignedBy - User ID of the assigner
 * @property {string} data.assignedAt - ISO timestamp of assignment
 * @property {string} data.reason - Reason for assignment
 */

/**
 * @typedef {Object} BulkAssignmentResult
 * @property {string} compId - Unique comp ID
 * @property {string} primaryFilmId - IMDB ID of the primary film
 * @property {string} compFilmId - IMDB ID of the comp film
 * @property {string} status - Status of the assignment ('success' or 'failed')
 */

/**
 * @typedef {Object} BulkAssignmentResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {number} data.totalProcessed - Total number of assignments processed
 * @property {number} data.successful - Number of successful assignments
 * @property {number} data.failed - Number of failed assignments
 * @property {BulkAssignmentResult[]} data.results - Array of assignment results
 * @property {string[]} data.errors - Array of error messages
 */

/**
 * @typedef {Object} CompUpdate
 * @property {string} reason - Updated reason for the comp
 * @property {boolean} isActive - Active status of the comp
 */

/**
 * @typedef {Object} UpdateResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {string} data.id - Unique comp ID
 * @property {string} data.reason - Updated reason
 * @property {boolean} data.isActive - Active status
 * @property {string} data.updatedAt - ISO timestamp of update
 */

/**
 * @typedef {Object} CompHistoryEntry
 * @property {string} id - Unique history entry ID
 * @property {string} action - Action performed ('created', 'updated', 'deleted')
 * @property {string} assignedBy - User ID of the person who performed the action
 * @property {string} reason - Reason for the action
 * @property {string} timestamp - ISO timestamp of the action
 */

/**
 * @typedef {Object} CompHistoryResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {string} data.compId - Unique comp ID
 * @property {CompHistoryEntry[]} data.history - Array of history entries
 */

/**
 * @typedef {Object} AnalyticsOverview
 * @property {number} totalComps - Total number of comps
 * @property {number} modeledComps - Number of modeled comps
 * @property {number} greenlightComps - Number of greenlight comps
 * @property {number} activeComps - Number of active comps
 * @property {number} inactiveComps - Number of inactive comps
 * @property {number} averageConfidenceScore - Average confidence score
 * @property {string} earliestComp - ISO timestamp of earliest comp
 * @property {string} latestComp - ISO timestamp of latest comp
 */

/**
 * @typedef {Object} MonthlyTrend
 * @property {string} month - ISO timestamp of the month
 * @property {number} compsCreated - Number of comps created in the month
 * @property {number} modeledCreated - Number of modeled comps created
 * @property {number} greenlightCreated - Number of greenlight comps created
 */

/**
 * @typedef {Object} TopUser
 * @property {string} assignedBy - User ID
 * @property {number} totalAssignments - Total number of assignments
 * @property {number} greenlightAssignments - Number of greenlight assignments
 */

/**
 * @typedef {Object} TopFilm
 * @property {string} filmId - IMDB ID of the film
 * @property {number} totalComps - Total number of comps
 * @property {number} modeledComps - Number of modeled comps
 * @property {number} greenlightComps - Number of greenlight comps
 */

/**
 * @typedef {Object} AnalyticsResponse
 * @property {boolean} success - Whether the request was successful
 * @property {Object} data - Response data
 * @property {AnalyticsOverview} data.overview - Overview statistics
 * @property {MonthlyTrend[]} data.monthlyTrends - Monthly trend data
 * @property {TopUser[]} data.topUsers - Top users by activity
 * @property {TopFilm[]} data.topFilms - Top films by comp count
 */

// ==================== VALIDATION FUNCTIONS ====================

/**
 * Validate IMDB ID format
 * @param {string} imdbId - IMDB ID to validate
 * @returns {boolean} - Whether the ID is valid
 */
export const validateIMDBId = (imdbId) => {
  if (!imdbId || typeof imdbId !== 'string') return false;
  return /^tt\d{7,8}$/.test(imdbId);
};

/**
 * Validate confidence score
 * @param {number} score - Confidence score to validate
 * @returns {boolean} - Whether the score is valid
 */
export const validateConfidenceScore = (score) => {
  return typeof score === 'number' && score >= 0 && score <= 1;
};

/**
 * Validate matching criteria
 * @param {MatchingCriteria} criteria - Matching criteria to validate
 * @returns {boolean} - Whether the criteria is valid
 */
export const validateMatchingCriteria = (criteria) => {
  if (!criteria || typeof criteria !== 'object') return false;
  
  const requiredFields = ['genre', 'creative', 'studio', 'seasonal', 'budget'];
  return requiredFields.every(field => 
    typeof criteria[field] === 'number' && 
    criteria[field] >= 0 && 
    criteria[field] <= 1
  );
};

/**
 * Validate comp assignment
 * @param {CompAssignment} assignment - Assignment to validate
 * @returns {boolean} - Whether the assignment is valid
 */
export const validateCompAssignment = (assignment) => {
  if (!assignment || typeof assignment !== 'object') return false;
  
  return validateIMDBId(assignment.primaryFilmId) &&
         validateIMDBId(assignment.compFilmId) &&
         typeof assignment.reason === 'string' &&
         assignment.reason.trim().length > 0 &&
         typeof assignment.assignedBy === 'string' &&
         assignment.assignedBy.trim().length > 0;
};

/**
 * Validate film comps response
 * @param {FilmCompsResponse} response - Response to validate
 * @returns {boolean} - Whether the response is valid
 */
export const validateFilmCompsResponse = (response) => {
  if (!response || typeof response !== 'object') return false;
  if (typeof response.success !== 'boolean') return false;
  if (!response.data || typeof response.data !== 'object') return false;
  
  const { data } = response;
  return validateIMDBId(data.filmId) &&
         typeof data.filmTitle === 'string' &&
         Array.isArray(data.modeledComps) &&
         Array.isArray(data.greenlightComps) &&
         typeof data.totalCount === 'number' &&
         typeof data.modeledCount === 'number' &&
         typeof data.greenlightCount === 'number';
};

/**
 * Validate comparison data response
 * @param {ComparisonDataResponse} response - Response to validate
 * @returns {boolean} - Whether the response is valid
 */
export const validateComparisonDataResponse = (response) => {
  if (!response || typeof response !== 'object') return false;
  if (typeof response.success !== 'boolean') return false;
  if (!response.data || typeof response.data !== 'object') return false;
  
  const { data } = response;
  return data.primaryFilm &&
         validateIMDBId(data.primaryFilm.filmId) &&
         Array.isArray(data.comps) &&
         data.summary &&
         typeof data.summary.totalComps === 'number';
};

// ==================== ERROR TYPES ====================

/**
 * @typedef {Object} APIError
 * @property {boolean} success - Always false for errors
 * @property {Object} error - Error details
 * @property {string} error.code - Error code
 * @property {string} error.message - Human readable error message
 * @property {Object} error.details - Additional error details
 * @property {string} error.details.field - Field that caused the error
 * @property {string} error.details.value - Value that caused the error
 */

/**
 * Common error codes
 */
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DUPLICATE_COMP: 'DUPLICATE_COMP',
  INVALID_FILM_ID: 'INVALID_FILM_ID',
  INVALID_COMP_ID: 'INVALID_COMP_ID'
};

// ==================== UTILITY TYPES ====================

/**
 * @typedef {Object} CompType
 * @property {string} MODELED - 'modeled'
 * @property {string} GREENLIGHT - 'greenlight'
 * @property {string} ALL - 'all'
 */
export const COMP_TYPES = {
  MODELED: 'modeled',
  GREENLIGHT: 'greenlight',
  ALL: 'all'
};

/**
 * @typedef {Object} TimelineAlignment
 * @property {string} DAYS - 'days'
 * @property {string} WEEKS - 'weeks'
 */
export const TIMELINE_ALIGNMENT = {
  DAYS: 'days',
  WEEKS: 'weeks'
};

/**
 * @typedef {Object} CompStatus
 * @property {string} ACTIVE - 'active'
 * @property {string} INACTIVE - 'inactive'
 */
export const COMP_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive'
};

// Export validation functions and constants for use in other modules
const compsTypes = {
  // Validation functions
  validateIMDBId,
  validateConfidenceScore,
  validateMatchingCriteria,
  validateCompAssignment,
  validateFilmCompsResponse,
  validateComparisonDataResponse,
  
  // Constants
  ERROR_CODES,
  COMP_TYPES,
  TIMELINE_ALIGNMENT,
  COMP_STATUS
};

export default compsTypes;
